/**
 * ServerDetailModal
 * 서버 상세보기 모달 (행 클릭 시 표시)
 */

import React, { useState } from 'react';
import type { DeploymentServer } from './ServerManagementPage';

interface OrgInfo {
  id: string;
  name: string;
  service_type?: string;
}

interface Props {
  server: DeploymentServer;
  orgMap: Record<string, OrgInfo>;
  onClose: () => void;
  onEdit: (server: DeploymentServer) => void;
  onDelete: (server: DeploymentServer) => void;
  onHealthCheck: (serverId: string) => void;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  online: { label: '온라인', color: 'text-green-800', bg: 'bg-green-100' },
  offline: { label: '오프라인', color: 'text-red-800', bg: 'bg-red-100' },
  unknown: { label: '미확인', color: 'text-gray-800', bg: 'bg-gray-100' },
  maintenance: { label: '점검중', color: 'text-yellow-800', bg: 'bg-yellow-100' },
};

type TabType = 'info' | 'network' | 'health';

const TABS: { key: TabType; label: string }[] = [
  { key: 'info', label: '기본 정보' },
  { key: 'network', label: '네트워크' },
  { key: 'health', label: '헬스체크' },
];

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getTimeSince = (dateStr?: string) => {
  if (!dateStr) return '확인 안됨';
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return '방금 전';
  if (minutes < 60) return `${minutes}분 전`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}시간 전`;
  const days = Math.floor(hours / 24);
  return `${days}일 전`;
};

const InfoRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div className="flex py-2 border-b border-gray-100 last:border-0">
    <dt className="w-32 flex-shrink-0 text-sm font-medium text-gray-500">{label}</dt>
    <dd className="text-sm text-gray-900">{value || '-'}</dd>
  </div>
);

export const ServerDetailModal: React.FC<Props> = ({
  server,
  orgMap,
  onClose,
  onEdit,
  onDelete,
  onHealthCheck,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('info');
  const statusInfo = STATUS_CONFIG[server.status] || STATUS_CONFIG.unknown;
  const linkedOrg = server.organization_id ? orgMap[server.organization_id] : null;

  const renderInfoTab = () => (
    <dl className="space-y-0">
      <InfoRow label="서버 코드" value={<span className="font-mono font-medium">{server.server_code}</span>} />
      <InfoRow label="고객사" value={server.customer_name} />
      <InfoRow
        label="연결 사업자"
        value={
          linkedOrg ? (
            <span className="text-indigo-600">
              {linkedOrg.name}
              {linkedOrg.service_type && <span className="text-gray-500 ml-1">({linkedOrg.service_type})</span>}
            </span>
          ) : (
            <span className="text-gray-400">미연결</span>
          )
        }
      />
      <InfoRow label="호스트명" value={server.hostname} />
      <InfoRow label="배포 모드" value={server.deploy_mode} />
      <InfoRow label="OS" value={server.os_info} />
      {server.hardware_info && (
        <>
          {server.hardware_info.cpu && <InfoRow label="CPU" value={server.hardware_info.cpu} />}
          {server.hardware_info.ram && <InfoRow label="RAM" value={server.hardware_info.ram} />}
          {server.hardware_info.disk && <InfoRow label="디스크" value={server.hardware_info.disk} />}
        </>
      )}
      <InfoRow label="설치일" value={server.installed_at ? formatDate(server.installed_at) : undefined} />
      <InfoRow label="등록일" value={formatDate(server.created_at)} />
      {server.updated_at && <InfoRow label="수정일" value={formatDate(server.updated_at)} />}
      <InfoRow
        label="메모"
        value={
          server.notes ? (
            <span className="whitespace-pre-wrap">{server.notes}</span>
          ) : undefined
        }
      />
    </dl>
  );

  const renderNetworkTab = () => (
    <dl className="space-y-0">
      <InfoRow
        label="Tailscale IP"
        value={
          server.tailscale_ip ? (
            <span className="font-mono">{server.tailscale_ip}</span>
          ) : undefined
        }
      />
      <InfoRow
        label="로컬 IP"
        value={
          server.local_ip ? (
            <span className="font-mono">{server.local_ip}</span>
          ) : undefined
        }
      />
      <InfoRow label="SSH 사용자" value={server.ssh_user} />
      <InfoRow label="SSH 포트" value={server.ssh_port?.toString()} />
      {server.docker_services && server.docker_services.length > 0 && (
        <InfoRow
          label="Docker 서비스"
          value={
            <div className="flex flex-wrap gap-1">
              {server.docker_services.map((svc) => (
                <span
                  key={svc}
                  className="inline-flex px-2 py-0.5 text-xs bg-blue-50 text-blue-700 rounded"
                >
                  {svc}
                </span>
              ))}
            </div>
          }
        />
      )}
    </dl>
  );

  const renderHealthTab = () => {
    const hc = server.last_health_check;

    return (
      <div className="space-y-4">
        <dl className="space-y-0">
          <InfoRow label="상태" value={
            <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${statusInfo.bg} ${statusInfo.color}`}>
              {statusInfo.label}
            </span>
          } />
          <InfoRow label="마지막 확인" value={
            server.last_seen_at ? (
              <span>
                {getTimeSince(server.last_seen_at)}
                <span className="text-gray-400 ml-2">({formatDate(server.last_seen_at)})</span>
              </span>
            ) : undefined
          } />
          {hc?.response_time_ms != null && (
            <InfoRow label="응답 시간" value={`${Math.round(hc.response_time_ms)}ms`} />
          )}
          {hc?.status && (
            <InfoRow label="체크 결과" value={hc.status} />
          )}
        </dl>

        {hc && Object.keys(hc).length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">상세 데이터 (JSON)</h4>
            <pre className="bg-gray-50 border rounded-lg p-3 text-xs text-gray-700 overflow-x-auto max-h-60 overflow-y-auto">
              {JSON.stringify(hc, null, 2)}
            </pre>
          </div>
        )}

        {!hc && (
          <div className="text-center py-6 text-gray-400">
            헬스체크 기록이 없습니다.
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* 헤더 */}
        <div className="flex justify-between items-center p-6 border-b">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-gray-900">{server.server_code}</h2>
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusInfo.bg} ${statusInfo.color}`}>
              {statusInfo.label}
            </span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
        </div>

        {/* 탭 */}
        <div className="border-b px-6">
          <div className="flex gap-0">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* 컨텐츠 */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'info' && renderInfoTab()}
          {activeTab === 'network' && renderNetworkTab()}
          {activeTab === 'health' && renderHealthTab()}
        </div>

        {/* 하단 버튼 */}
        <div className="flex justify-between items-center p-6 border-t bg-gray-50 rounded-b-lg">
          <div className="flex gap-2">
            <button
              onClick={() => onHealthCheck(server.id)}
              className="px-3 py-2 text-sm bg-green-50 text-green-700 rounded-lg hover:bg-green-100 border border-green-200"
            >
              상태 확인
            </button>
            <button
              onClick={() => onEdit(server)}
              className="px-3 py-2 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 border border-blue-200"
            >
              수정
            </button>
            <button
              onClick={() => onDelete(server)}
              className="px-3 py-2 text-sm bg-red-50 text-red-700 rounded-lg hover:bg-red-100 border border-red-200"
            >
              삭제
            </button>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border rounded-lg text-gray-700 hover:bg-gray-100"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
};
